<template>
  <MainHeader />
  <MainSidebar />
  <div
    class="main-content bg_gray d-flex flex-column transition overflow-hidden"
  >
    <BreadcrumbMenu
      pageTitle="edit_purchase"
      :items="breadcrumbItems"
      :showHome="false"
    />
    <div class="row mb-40">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.date") }}</label>
          <input
            type="date"
            v-model="form.date"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.supplier") }}</label>
          <select v-model="form.supplier_id" class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55 ps-3">
            <option :value="null">{{ $t("purchase_page.select_supplier") }}</option>
            <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
              {{ supplier.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.warehouse") }}</label>
          <select v-model="form.warehouse_id" class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55 ps-3">
            <option :value="null">{{ $t("purchase_page.select_warehouse") }}</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
              {{ warehouse.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-12">
        <div class="form-group mb-25 position-relative">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.choose_product") }}</label>
          <div class="search-area style-two position-relative w-100">
            <input
              type="text"
              v-model="searchQuery"
              @input="handleSearch"
              :placeholder="$t('purchase_page.scan_search_product')"
              class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white pe-3"
            />
            <button
              type="button"
              class="bg-transparent border-0 position-absolute h-100 pt-0 py-0 px-2 pointer-events-none"
              tabindex="-1"
              aria-hidden="true"
            >
              <img src="../../assets/img/icons/search.svg" alt="Image" />
            </button>
          </div>

          <div
            v-if="showResults && searchResults.length > 0"
            class="search-results-dropdown position-absolute w-100 bg-white shadow-sm rounded-1 z-3 mt-1 overflow-auto"
            style="max-height: 300px;"
          >
            <div
                v-for="product in searchResults" 
                :key="product.id"
                @click="addProduct(product)"
                class="search-result-item p-3 border-bottom cursor-pointer hover-bg-light"
              >
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="fw-bold fs-14 text-black">{{ product.name }}</div>
                  <div class="fs-12 text-muted">{{ product.code }}</div>
                </div>
                <div class="text-end">
                  <div class="text-primary fw-bold">${{ product.cost }}</div>
                  <div class="fs-12 text-muted">{{ $t("purchase_page.stock") }}: {{ getProductStock(product) }}</div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="showResults && normalizedSearchQuery.length >= 1"
            class="search-results-dropdown position-absolute w-100 bg-white shadow-sm rounded-1 z-3 mt-1 p-3 text-center text-muted"
          >
            {{ $t("purchase_page.no_standard_products_found") }}
          </div>
        </div>
      </div>
    </div>
    <SelectedProducts 
      :products="selectedProducts" 
      @remove="removeProduct"
    />
    <SubmitPurchase 
      v-model:orderTax="form.order_tax"
      v-model:discount="form.discount"
      v-model:shipping="form.shipping"
      v-model:status="form.status"
      v-model:notes="form.notes"
      v-model:paidAmount="form.paid_amount"
      v-model:paymentMethodId="form.payment_method_id"
      :paymentMethods="paymentMethods"
      :calculatedTax="calculatedTax"
      :grandTotal="grandTotal"
      @submit="handleSubmit"
    />

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <div
    class="delete-popup offcanvas offcanvas-end border-0"
    tabindex="-1"
    id="deletePopup"
  >
    <div class="offcanvas-body p-0">
      <div class="delete-success">
        <img src="../../assets/img/icons/tick-circle.svg" alt="Image" />
        <span class="text-white fw-medium">
          {{ $t("purchase_page.product_deleted_from_list") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import SelectedProducts from "../../components/Purchases/CreatePurchase/SelectedProducts.vue";
import SubmitPurchase from "../../components/Purchases/CreatePurchase/SubmitPurchase.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";

import supplierService, { Supplier } from "../../services/supplier";
import warehouseService, { Warehouse } from "../../services/warehouse";
import productService, { Product } from "../../services/product";
import { getPurchase, updatePurchase } from "../../services/purchase";
import paymentMethodService, { PaymentMethod } from "../../services/paymentMethod";

type SelectedPurchaseProduct = Product & {
  original_qty?: number;
  quantity: number;
  tax: number;
  discount: number;
  subTotal: string;
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeSearchText = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

const createLineSubtotal = (product: Partial<SelectedPurchaseProduct>): number => {
  const unitCost = toNumber(product.cost);
  const quantity = toNumber(product.quantity);
  const discount = toNumber(product.discount);
  const tax = toNumber(product.tax);

  return unitCost * quantity - discount + tax;
};

export default defineComponent({
  name: "EditPurchasePage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    SelectedProducts,
    SubmitPurchase,
    MainFooter,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const purchaseId = route.params.id as string;

    const suppliers = ref<Supplier[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const paymentMethods = ref<PaymentMethod[]>([]);
    const selectedProducts = ref<SelectedPurchaseProduct[]>([]);
    const searchQuery = ref("");
    const searchResults = ref<Product[]>([]);
    const showResults = ref(false);
    const isSubmitting = ref(false);
    const breadcrumbItems = [
      { label: "purchase_list", to: "/purchase-list" },
      { label: "edit_purchase" },
    ];

    const form = reactive({
      date: "",
      supplier_id: null as number | null,
      warehouse_id: null as number | null,
      order_tax: 0,
      discount: 0,
      shipping: 0,
      status: "received",
      notes: "",
      paid_amount: 0,
      payment_method_id: null as number | null,
    });

    const normalizedSearchQuery = computed(() => normalizeSearchText(searchQuery.value));

    const getProductStock = (product: Partial<Product> & { stocks?: Product["stocks"] }) => {
      if (!form.warehouse_id || !product.stocks?.length) {
        return 0;
      }

      const stock = product.stocks.find((entry) => Number(entry.warehouse_id) === Number(form.warehouse_id));
      return toNumber(stock?.qty ?? stock?.quantity);
    };

    const syncSelectedProductStocks = async () => {
      if (!selectedProducts.value.length) {
        return;
      }

      const stockAwareProducts = await Promise.all(
        selectedProducts.value.map(async (product) => {
          if (!product.id) {
            return product;
          }

          try {
            const latestProduct = await productService.getProduct(product.id);
            const stockQty = getProductStock(latestProduct);

            return {
              ...product,
              stocks: latestProduct.stocks,
              qty: stockQty,
              original_qty: stockQty,
            };
          } catch (error) {
            console.error(`Failed to refresh stock for product ${product.id}:`, error);
            return {
              ...product,
              qty: 0,
              original_qty: 0,
            };
          }
        })
      );

      selectedProducts.value = stockAwareProducts;
    };

    const fetchData = async () => {
      try {
        const [supplierRes, warehouseRes, methodRes, purchaseRes] = await Promise.all([
          supplierService.getSuppliers({ per_page: 1000 }),
          warehouseService.getWarehouses(),
          paymentMethodService.getPaymentMethods(),
          getPurchase(purchaseId)
        ]);

        suppliers.value = supplierRes.data;
        warehouses.value = warehouseRes;
        paymentMethods.value = methodRes;

        // Fill form with purchase data
        if (purchaseRes) {
          form.date = purchaseRes.date || purchaseRes.created_at?.split("T")[0] || "";
          form.supplier_id = purchaseRes.supplier_id;
          form.warehouse_id = purchaseRes.warehouse_id;
          form.order_tax = Number(purchaseRes.tax_rate ?? 0);
          form.discount = Number(purchaseRes.discount || 0);
          form.shipping = Number(purchaseRes.shipping || 0);
          form.status = purchaseRes.status;
          form.notes = purchaseRes.note || "";
          form.paid_amount = Number(purchaseRes.paid);
          
          if (purchaseRes.payments && purchaseRes.payments.length > 0) {
            form.payment_method_id = purchaseRes.payments[0].method_id;
          }

          selectedProducts.value = purchaseRes.items.map((item: any) => ({
            ...item.product,
            id: item.product_id, // Ensure we use product_id
            code: item.product_code,
            name: item.product_name,
            cost: Number(item.unit_cost),
            quantity: Number(item.quantity),
            tax: Number(item.tax),
            discount: Number(item.discount),
            subTotal: Number(item.subtotal).toFixed(2)
          }));

          await syncSelectedProductStocks();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        await Swal.fire(t("purchase_page.error"), t("purchase_page.failed_to_load_purchase_details"), "error");
      }
    };

    onMounted(fetchData);

    const subtotal = computed(() => {
      return selectedProducts.value.reduce((sum, product) => sum + createLineSubtotal(product), 0);
    });

    const calculatedTax = computed(() => subtotal.value * (toNumber(form.order_tax) / 100));
    const grandTotal = computed(() => subtotal.value + calculatedTax.value + toNumber(form.shipping) - toNumber(form.discount));

    const handleSearch = async () => {
      const searchTerm = normalizedSearchQuery.value;

      if (searchTerm.length < 1) {
        searchResults.value = [];
        showResults.value = false;
        return;
      }

      try {
        const results = await productService.getProducts({
          type: 1,
          search: searchTerm,
          warehouse_id: form.warehouse_id || undefined,
        });

        const searchNeedle = searchTerm.toLowerCase();
        searchResults.value = results.filter((product) => {
          const productName = normalizeSearchText(product.name || "").toLowerCase();
          const productCode = normalizeSearchText(product.code || "").toLowerCase();

          return productName.includes(searchNeedle) || productCode.includes(searchNeedle);
        });
        showResults.value = true;
      } catch (error) {
        console.error("Search error:", error);
        searchResults.value = [];
        showResults.value = true;
      }
    };

    const addProduct = (product: Product) => {
      const existing = selectedProducts.value.find(p => p.id === product.id);
      if (existing) {
        existing.quantity++;
        existing.subTotal = createLineSubtotal(existing).toFixed(2);
      } else {
        selectedProducts.value.push({
          ...product,
          qty: getProductStock(product),
          original_qty: getProductStock(product),
          quantity: 1,
          tax: 0,
          discount: 0,
          subTotal: createLineSubtotal({
            cost: toNumber(product.cost),
            quantity: 1,
            tax: 0,
            discount: 0,
          }).toFixed(2)
        });
      }
      searchQuery.value = "";
      showResults.value = false;
    };

    watch(() => form.warehouse_id, async () => {
      await handleSearch();
      await syncSelectedProductStocks();
    });

    const removeProduct = (index: number) => {
      selectedProducts.value.splice(index, 1);
    };

    const handleSubmit = async () => {
      if (isSubmitting.value) return;
      if (!form.supplier_id || !form.warehouse_id || selectedProducts.value.length === 0) {
        await Swal.fire(t("purchase_page.error"), t("purchase_page.fill_required_fields_and_product"), "error");
        return;
      }

      try {
        isSubmitting.value = true;
        const selectedWarehouse = warehouses.value.find(w => w.id === form.warehouse_id);
        const selectedSupplier = suppliers.value.find(s => s.id === form.supplier_id);
        const selectedPaymentMethod = paymentMethods.value.find(m => m.id === form.payment_method_id);

        let paymentStatus = "pending";
        if (form.paid_amount >= grandTotal.value) {
          paymentStatus = "paid";
        } else if (form.paid_amount > 0) {
          paymentStatus = "partial";
        }

        const payload = {
          ...form,
          grand_total: grandTotal.value,
          paid: form.paid_amount,
          method_id: form.payment_method_id,
          paid_by: selectedPaymentMethod ? selectedPaymentMethod.name.toLowerCase() : "cash",
          warehouse_code: selectedWarehouse?.code || "",
          warehouse_name: selectedWarehouse?.name || "",
          supplier_code: selectedSupplier?.code || "",
          supplier_name: selectedSupplier?.name || "",
          payment_status: paymentStatus,
          items: selectedProducts.value.map((p) => {
            const itemSubtotal = createLineSubtotal(p);
            return {
              product_id: p.id,
              product_code: p.code,
              product_name: p.name,
              unit_cost: p.cost,
              quantity: p.quantity,
              tax: p.tax,
              discount: p.discount,
              subtotal: itemSubtotal,
              grand_total: itemSubtotal,
            };
          }),
        };

        await updatePurchase(purchaseId, payload);
        await Swal.fire(t("purchase_page.success"), t("purchase_page.purchase_updated_successfully"), "success");
        router.push("/purchase-list");
      } catch (error) {
        console.error("Error updating purchase:", error);
        await Swal.fire(t("purchase_page.error"), t("purchase_page.failed_to_update_purchase"), "error");
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      suppliers,
      warehouses,
      paymentMethods,
      selectedProducts,
      searchQuery,
      searchResults,
      showResults,
      form,
      normalizedSearchQuery,
      subtotal,
      calculatedTax,
      grandTotal,
      handleSearch,
      addProduct,
      removeProduct,
      handleSubmit,
      isSubmitting,
      getProductStock,
      breadcrumbItems,
    };
  },
});
</script>
